/* ═══════════════════════════════════════════════════════════════
   SWINDLE KING — theme interactions (scoped, vanilla, no deps)
   Idempotent init — safe on DOMContentLoaded and shopify:section:load.
   All listeners passive; honors prefers-reduced-motion.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var io = null;
  if (!reduced && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          var el = e.target;
          var d = el.getAttribute('data-rd');
          if (d) el.style.transitionDelay = d + 'ms';
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
  }

  function initReveals(root) {
    var els = root.querySelectorAll('.skc-reveal:not([data-skc-obs]), .skc-ln:not([data-skc-obs]), .skc-clip:not([data-skc-obs])');
    els.forEach(function (el) {
      el.setAttribute('data-skc-obs', '1');
      if (io) { io.observe(el); } else { el.classList.add('is-in'); }
    });
  }

  function initHero(root) {
    root.querySelectorAll('.skc-hero:not([data-skc-init])').forEach(function (sec) {
      sec.setAttribute('data-skc-init', '1');
      var img = sec.querySelector('.skc-hero__media img');
      if (!img || reduced) return;
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var y = Math.min(window.scrollY, 900);
          img.style.setProperty('--skc-hy', (y * 0.06).toFixed(1) + 'px');
          ticking = false;
        });
      }, { passive: true });
    });
  }

  function initReign(root) {
    root.querySelectorAll('.skc-reign:not([data-skc-init])').forEach(function (sec) {
      sec.setAttribute('data-skc-init', '1');
      var swatches = sec.querySelectorAll('.skc-swatch');
      var imgs = sec.querySelectorAll('.skc-reign__img');
      var nameEl = sec.querySelector('.skc-reign__name');
      var priceEl = sec.querySelector('.skc-reign__price');
      var linkEl = sec.querySelector('.skc-reign__link');
      swatches.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var i = parseInt(btn.getAttribute('data-reign'), 10);
          if (!imgs[i]) return;
          swatches.forEach(function (b, j) {
            b.classList.toggle('is-active', j === i);
            b.setAttribute('aria-selected', j === i ? 'true' : 'false');
          });
          imgs.forEach(function (img, j) { img.classList.toggle('is-on', j === i); });
          var tint = imgs[i].getAttribute('data-tint');
          if (tint) sec.style.setProperty('--skc-reign-tint', tint);
          if (nameEl) nameEl.textContent = imgs[i].getAttribute('data-name');
          if (priceEl) priceEl.textContent = imgs[i].getAttribute('data-price');
          if (linkEl) linkEl.setAttribute('href', imgs[i].getAttribute('data-href'));
        });
      });
    });
  }

  function initScrub(root) {
    root.querySelectorAll('.skc-story__scrub:not([data-skc-init])').forEach(function (scrub) {
      scrub.setAttribute('data-skc-init', '1');
      var goldRaw = scrub.getAttribute('data-gold') || '';
      var goldWords = goldRaw.split('|').map(function (w) { return w.trim().toLowerCase(); }).filter(Boolean);
      var words = scrub.textContent.trim().split(/\s+/);
      scrub.textContent = '';
      var spans = words.map(function (w) {
        var s = document.createElement('span');
        s.className = 'skc-w' + (goldWords.indexOf(w.toLowerCase()) > -1 ? ' skc-w--gold' : '');
        s.textContent = w;
        scrub.appendChild(s);
        scrub.appendChild(document.createTextNode(' '));
        return s;
      });
      if (reduced) {
        spans.forEach(function (s) { s.classList.add('is-lit'); });
        return;
      }
      var tick = false;
      var onScrub = function () {
        if (tick) return;
        tick = true;
        requestAnimationFrame(function () {
          var r = scrub.getBoundingClientRect();
          var vh = window.innerHeight;
          var p = (vh * 0.85 - r.top) / (r.height + vh * 0.45);
          p = Math.max(0, Math.min(1, p));
          var lit = Math.round(p * spans.length);
          spans.forEach(function (s, i) { s.classList.toggle('is-lit', i < lit); });
          tick = false;
        });
      };
      window.addEventListener('scroll', onScrub, { passive: true });
      onScrub();
    });
  }

  function initRail(root) {
    root.querySelectorAll('.skc-rail:not([data-skc-init])').forEach(function (rail) {
      rail.setAttribute('data-skc-init', '1');
      var down = false, startX = 0, startL = 0, moved = false;
      rail.addEventListener('pointerdown', function (e) {
        down = true; moved = false;
        startX = e.clientX; startL = rail.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 6) { moved = true; rail.classList.add('is-drag'); }
        if (moved) rail.scrollLeft = startL - dx;
      }, { passive: true });
      window.addEventListener('pointerup', function () {
        down = false;
        rail.classList.remove('is-drag');
      });
      rail.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); moved = false; }
      }, true);
    });
  }

  function initNav(root) {
    root.querySelectorAll('.skc-header:not([data-skc-init])').forEach(function (header) {
      header.setAttribute('data-skc-init', '1');
      var burger = header.querySelector('.skc-header__burger');
      if (!burger) return;
      var setOpen = function (open) {
        header.classList.toggle('nav-open', open);
        document.body.style.overflow = open ? 'hidden' : '';
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      };
      burger.addEventListener('click', function () {
        setOpen(!header.classList.contains('nav-open'));
      });
      header.querySelectorAll('.skc-header__nav a').forEach(function (a) {
        a.addEventListener('click', function () { setOpen(false); });
      });
    });
  }

  function initQty(q) {
    if (q.getAttribute('data-skc-qty')) return;
    q.setAttribute('data-skc-qty', '1');
    var inp = q.querySelector('input');
    if (!inp) return;
    var min = parseInt(inp.getAttribute('min') || '1', 10);
    q.querySelectorAll('button[data-d]').forEach(function (b) {
      b.addEventListener('click', function () {
        var d = parseInt(b.getAttribute('data-d'), 10) || 0;
        var v = (parseInt(inp.value, 10) || min) + d;
        if (v < min) v = min;
        inp.value = v;
        inp.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function initPDP(root) {
    root.querySelectorAll('.skc-pdp:not([data-skc-init])').forEach(function (sec) {
      sec.setAttribute('data-skc-init', '1');
      var dataEl = sec.querySelector('[data-skc-variants]');
      var variants = [];
      try { variants = dataEl ? JSON.parse(dataEl.textContent) : []; } catch (err) {}
      var form = sec.querySelector('form[action*="/cart/add"]');
      var idInput = form ? form.querySelector('input[name="id"]') : null;
      var priceEl = sec.querySelector('[data-skc-price]');
      var btn = sec.querySelector('[data-skc-atc]');
      var btnSpan = btn ? (btn.querySelector('span') || btn) : null;
      var main = sec.querySelector('.skc-pdp__main img');
      var cur = (priceEl && priceEl.getAttribute('data-skc-cur')) || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      var showCode = priceEl && priceEl.getAttribute('data-skc-code') === '1';
      function money(c) {
        var v = c / 100;
        var whole = v % 1 === 0;
        var out;
        try {
          out = new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2
          }).format(v);
        } catch (err) {
          out = '$' + (whole ? v : v.toFixed(2));
        }
        return showCode ? out + ' ' + cur : out;
      }
      function current() {
        var sel = [];
        sec.querySelectorAll('[data-skc-opt]').forEach(function (g) {
          var ch = g.querySelector('input:checked');
          sel.push(ch ? ch.value : null);
        });
        if (!sel.length) return variants[0];
        var keys = ['option1', 'option2', 'option3'];
        for (var i = 0; i < variants.length; i++) {
          var ok = true;
          for (var j = 0; j < sel.length; j++) {
            if (sel[j] !== null && variants[i][keys[j]] !== sel[j]) { ok = false; break; }
          }
          if (ok) return variants[i];
        }
        return null;
      }
      function apply() {
        sec.querySelectorAll('[data-skc-opt]').forEach(function (g) {
          var ch = g.querySelector('input:checked');
          var b = g.querySelector('[data-skc-optval]');
          if (ch && b) b.textContent = ch.value;
        });
        var v = current();
        if (!v) {
          if (btn) { btn.disabled = true; if (btnSpan) btnSpan.textContent = 'Unavailable'; }
          return;
        }
        if (idInput) idInput.value = v.id;
        if (priceEl) {
          priceEl.innerHTML = (v.compare_at_price && v.compare_at_price > v.price)
            ? money(v.price) + ' <s>' + money(v.compare_at_price) + '</s>'
            : money(v.price);
        }
        if (btn) {
          btn.disabled = !v.available;
          if (btnSpan) btnSpan.textContent = v.available ? (btn.getAttribute('data-txt-add') || 'Add to Cart') : 'Sold Out';
        }
        if (v.featured_image && v.featured_image.src && main) {
          main.removeAttribute('srcset');
          main.src = v.featured_image.src + (v.featured_image.src.indexOf('?') > -1 ? '&' : '?') + 'width=1200';
          sec.querySelectorAll('.skc-pdp__thumb').forEach(function (x) { x.classList.remove('is-on'); });
        }
      }
      sec.querySelectorAll('[data-skc-opt] input').forEach(function (i) {
        i.addEventListener('change', apply);
      });
      sec.querySelectorAll('.skc-pdp__thumb').forEach(function (t) {
        t.addEventListener('click', function () {
          sec.querySelectorAll('.skc-pdp__thumb').forEach(function (x) { x.classList.remove('is-on'); });
          t.classList.add('is-on');
          if (main) { main.removeAttribute('srcset'); main.src = t.getAttribute('data-src'); }
        });
      });
      sec.querySelectorAll('.skc-qty').forEach(initQty);
    });
  }

  function initCart(root) {
    root.querySelectorAll('.skc-cart:not([data-skc-init])').forEach(function (sec) {
      sec.setAttribute('data-skc-init', '1');
      var form = sec.querySelector('form[action*="/cart"]');
      sec.querySelectorAll('.skc-qty').forEach(initQty);
      sec.querySelectorAll('.skc-qty input').forEach(function (i) {
        var t = null;
        i.addEventListener('change', function () {
          if (t) clearTimeout(t);
          t = setTimeout(function () { if (form) form.submit(); }, 350);
        });
      });
    });
  }

  function initAll() {
    var root = document;
    initReveals(root);
    initHero(root);
    initReign(root);
    initScrub(root);
    initRail(root);
    initNav(root);
    initPDP(root);
    initCart(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();